import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PmsSettingsPage } from "./PmsSettingsPage";
import { SyncDashboardPage } from "./SyncDashboardPage";
import { SyncStatsPage } from "./SyncStatsPage";

const PMS_TABS = ["settings", "sync", "stats"] as const;
type PmsTab = (typeof PMS_TABS)[number];

const isPmsTab = (value: string | null): value is PmsTab =>
  value !== null && (PMS_TABS as readonly string[]).includes(value);

export const PmsPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");

  // Seed the active tab from the URL so deep links (e.g. a "PMS sync failed"
  // notification → ?tab=sync) open the right tab instead of a blank page.
  const [activeTab, setActiveTab] = useState<PmsTab>(
    isPmsTab(tabParam) ? tabParam : "settings",
  );

  // Keep deep links working when the page is already mounted. Only react to a
  // valid tab value — SyncDashboardPage rewrites the query string for its own
  // filters, so ignore param removal to avoid resetting the active tab.
  useEffect(() => {
    if (isPmsTab(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  return (
    <div className="flex flex-col gap-6 p-6">
      <h1 className="text-2xl font-semibold">{t("pms.title")}</h1>
      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          if (isPmsTab(value)) {
            setActiveTab(value);
          }
        }}
      >
        <TabsList>
          <TabsTrigger value="settings">{t("pms.tabs.settings")}</TabsTrigger>
          <TabsTrigger value="sync">{t("pms.tabs.sync")}</TabsTrigger>
          <TabsTrigger value="stats">{t("pms.tabs.stats")}</TabsTrigger>
        </TabsList>
        <TabsContent value="settings">
          <PmsSettingsPage />
        </TabsContent>
        <TabsContent value="sync">
          <SyncDashboardPage />
        </TabsContent>
        <TabsContent value="stats">
          <SyncStatsPage />
        </TabsContent>
      </Tabs>
    </div>
  );
};
